/* __next_internal_client_entry_do_not_use__ Default,ExpandableContent auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getTestProps } from 'lib/testing/utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import useDictionary from 'lib/hooks/useDictionary';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const TextContentBlock = (props)=>{
    const { RenderingIdentifier, selectTheme, layout, smallContainer } = props?.params || {};
    const { title, subTitle, description, cta1, cta2, removeBottomPadding, reduceBottomPadding } = props?.fields || {};
    const theme = selectTheme;
    const layoutVariant = layout || 'FullWidth';
    const GAComponentName = layoutVariant === 'FullWidth' ? 'Full Width Content Block' : 'Text Content Block';
    const hasContent = Boolean(title?.value || subTitle?.value);
    const hasCtas = Boolean(cta1?.value?.href || cta2?.value?.href);
    const noPaddingBottom = Boolean(removeBottomPadding?.value);
    const partialBottomPadding = Boolean(reduceBottomPadding?.value);
    const isSmallContainer = smallContainer === '1';
    const isEditing = useIsEditing();
    const styles = parseStyleParams(props?.params, [
        'cta1',
        'cta2'
    ]);
    const { base, contentWrapper, titleText, subTitleText, descriptionText, divider, ctaContainer, ctaWrapper } = TAILWIND_VARIANTS();
    if (!description?.value && !title?.value && !subTitle?.value && !cta1?.value?.href && !cta2?.value?.href && !isEditing) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    /*
   * RENDERING
   */ return /*#__PURE__*/ _jsx("div", {
        "data-component": "authorable/shared/content/text-content-block",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-text-content-block-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("div", {
                className: base({
                    layout: layoutVariant,
                    removeBottomPadding: noPaddingBottom,
                    reduceBottomPadding: partialBottomPadding
                }),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentWrapper({
                            layout: layoutVariant,
                            hasContent,
                            isSmallContainer
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: titleText(),
                                field: title,
                                tag: "h2"
                            }),
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: subTitleText(),
                                field: subTitle,
                                tag: "p"
                            }),
                            hasContent && /*#__PURE__*/ _jsx("div", {
                                className: divider()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentWrapper({
                            layout: layoutVariant,
                            hasContent,
                            isSmallContainer
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                className: descriptionText(),
                                field: description
                            }),
                            hasCtas && /*#__PURE__*/ _jsx("div", {
                                className: ctaContainer({
                                    layout: layoutVariant
                                }),
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: ctaWrapper({
                                        layout: layoutVariant
                                    }),
                                    children: [
                                        cta1?.value && /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "component-card-item-button-1-color",
                                            ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                            field: cta1,
                                            ctaVariant: theme === 'ThemesLight' ? 'primaryOnCanvas' : 'primary',
                                            ctaIcon: "arrow-right",
                                            "aria-label": title?.value?.trim() && cta1?.value?.text ? `${cta1.value.text} - ${title.value}` : undefined,
                                            ...getTestProps(`primary-cta`),
                                            gtmEvent: createCTAClickGTMEvent({
                                                header: title?.value,
                                                ctaText: cta1?.value?.text,
                                                ctaType: 'blue',
                                                componentName: GAComponentName,
                                                ctaLink: cta1?.value?.href
                                            })
                                        }),
                                        cta2?.value && /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "component-card-item-button-2-color",
                                            ctaStyle: getCtaStyle(styles.cta2, 'secondary'),
                                            field: cta2,
                                            ctaIcon: "arrow-right",
                                            "aria-label": title?.value?.trim() && cta2?.value?.text ? `${cta2.value.text} - ${title.value}` : undefined,
                                            ...getTestProps(`secondary-cta`),
                                            gtmEvent: createCTAClickGTMEvent({
                                                header: title?.value,
                                                ctaText: cta2?.value?.text,
                                                ctaType: 'clear',
                                                componentName: GAComponentName,
                                                ctaLink: cta2?.value?.href
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
};
const ExpandableContentVariant = (props)=>{
    const { RenderingIdentifier, selectTheme, layout, smallContainer } = props?.params || {};
    const { title, subTitle, description, cta1, cta2, removeBottomPadding, reduceBottomPadding, additionalDescription } = props?.fields || {};
    const [isExpanded, setIsExpanded] = useState(false);
    const theme = selectTheme;
    const layoutVariant = layout || 'FullWidth';
    const GAComponentName = layoutVariant === 'FullWidth' ? 'Full Width Content Block' : 'Text Content Block';
    const hasContent = Boolean(title?.value || subTitle?.value);
    const hasCtas = Boolean(cta1?.value?.href || cta2?.value?.href);
    const noPaddingBottom = Boolean(removeBottomPadding?.value);
    const partialBottomPadding = Boolean(reduceBottomPadding?.value);
    const isSmallContainer = smallContainer === '1';
    const { getDictionaryValue } = useDictionary();
    const ContinueReadingText = getDictionaryValue('ContinueReading');
    const isEditing = useIsEditing();
    const styles = parseStyleParams(props?.params, [
        'cta1',
        'cta2'
    ]);
    const { base, contentWrapper, expandableTitleText, subTitleText, descriptionText, divider, ctaContainer, ctaWrapper, continueReadingButton, contentWrapperInner, expanddescriptionText } = TAILWIND_VARIANTS();
    if (!description?.value && !title?.value && !subTitle?.value && !cta1?.value?.href && !cta2?.value?.href && !isEditing) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    /*
   * RENDERING
   */ return /*#__PURE__*/ _jsx("div", {
        "data-component": "authorable/shared/content/text-content-block",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-text-content-block-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("div", {
                className: base({
                    layout: layoutVariant,
                    removeBottomPadding: noPaddingBottom,
                    reduceBottomPadding: partialBottomPadding
                }),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentWrapper({
                            layout: layoutVariant,
                            hasContent,
                            isSmallContainer
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: expandableTitleText(),
                                field: title,
                                tag: "h2"
                            }),
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: subTitleText(),
                                field: subTitle,
                                tag: "p"
                            }),
                            hasContent && /*#__PURE__*/ _jsx("div", {
                                className: divider()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: contentWrapperInner({
                            layout: layoutVariant,
                            hasContent,
                            isSmallContainer
                        }),
                        children: [
                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                className: descriptionText(),
                                field: description
                            }),
                            !isExpanded && /*#__PURE__*/ _jsx(ButtonWrapper, {
                                ctaComponentClass: "default",
                                ctaVariant: "tertiary",
                                onClick: ()=>setIsExpanded(true),
                                className: continueReadingButton(),
                                remixIcon: "arrow-down-line",
                                children: /*#__PURE__*/ _jsx("span", {
                                    children: ContinueReadingText
                                })
                            }),
                            isExpanded && /*#__PURE__*/ _jsx(RichTextWrapper, {
                                className: expanddescriptionText(),
                                field: additionalDescription
                            }),
                            hasCtas && /*#__PURE__*/ _jsx("div", {
                                className: ctaContainer({
                                    layout: layoutVariant
                                }),
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: ctaWrapper({
                                        layout: layoutVariant
                                    }),
                                    children: [
                                        cta1?.value && /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "component-card-item-button-1-color",
                                            ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                            field: cta1,
                                            ctaVariant: theme === 'ThemesLight' ? 'primaryOnCanvas' : 'primary',
                                            ctaIcon: "arrow-right",
                                            "aria-label": title?.value?.trim() && cta1?.value?.text ? `${cta1.value.text} - ${title.value}` : undefined,
                                            ...getTestProps(`primary-cta`),
                                            gtmEvent: createCTAClickGTMEvent({
                                                header: title?.value,
                                                ctaText: cta1?.value?.text,
                                                ctaType: 'blue',
                                                componentName: GAComponentName,
                                                ctaLink: cta1?.value?.href
                                            })
                                        }),
                                        cta2?.value && /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaComponentClass: "component-card-item-button-2-color",
                                            ctaStyle: getCtaStyle(styles.cta2, 'secondary'),
                                            field: cta2,
                                            ctaIcon: "arrow-right",
                                            "aria-label": title?.value?.trim() && cta2?.value?.text ? `${cta2.value.text} - ${title.value}` : undefined,
                                            ...getTestProps(`secondary-cta`),
                                            gtmEvent: createCTAClickGTMEvent({
                                                header: title?.value,
                                                ctaText: cta2?.value?.text,
                                                ctaType: 'clear',
                                                componentName: GAComponentName,
                                                ctaLink: cta2?.value?.href
                                            })
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
};
export const Default = withStandardComponentWrapper(TextContentBlock, false);
export const ExpandableContent = withStandardComponentWrapper(ExpandableContentVariant, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'flex',
            'bg-component-section-bg',
            'gap-component-section-container-gutter',
            'relative'
        ],
        contentWrapper: [
            'flex',
            'flex-col',
            '!w-full',
            'h-auto',
            'ml-auto',
            'mr-auto'
        ],
        titleText: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'leading-typography-line-height-xlarge'
        ],
        expandableTitleText: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-small',
            'leading-typography-line-height-small'
        ],
        subTitleText: [
            'text-component-section-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large'
        ],
        descriptionText: [],
        divider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        ctaContainer: [
            'flex',
            'flex-col',
            'w-full'
        ],
        ctaWrapper: [
            'flex',
            'gap-spacing-spacing-16'
        ],
        continueReadingButton: [
            'mt-[16px]',
            'mb-[56px]'
        ],
        contentWrapperInner: [
            'flex',
            'flex-col',
            'w-full',
            'h-auto',
            'gap-0'
        ],
        expanddescriptionText: [
            'py-spacing-spacing-40'
        ]
    },
    variants: {
        backgroundLayout: {
            FullWidth: {
                base: [
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:bg-component-section-bg',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:right-[calc(-50vw+50%)]'
                ]
            },
            Content: {
                base: []
            }
        },
        layout: {
            SideBySide: {
                base: [
                    'flex-col',
                    'lg:flex-row',
                    'py-component-section-container-padding-y'
                ],
                contentWrapper: [
                    'lg:w-1/2',
                    'gap-space-between-base'
                ],
                ctaContainer: [
                    'lg:justify-start'
                ],
                ctaWrapper: [
                    'flex-col',
                    'md:flex-row',
                    'lg:gap-spacing-spacing-16'
                ]
            },
            FullWidth: {
                base: [
                    'flex-col',
                    'py-component-section-container-padding-y'
                ],
                contentWrapper: [
                    'gap-component-section-container-gutter'
                ],
                ctaContainer: [
                    'justify-start'
                ],
                ctaWrapper: [
                    'flex-col',
                    'md:flex-row',
                    'gap-spacing-spacing-16'
                ]
            }
        },
        hasContent: {
            true: {
                contentWrapper: 'gap-space-between-base'
            },
            false: {
                contentWrapper: 'gap-0'
            }
        },
        removeBottomPadding: {
            true: {
                base: [
                    '!pb-0'
                ]
            },
            false: {
                base: [
                    ''
                ]
            }
        },
        reduceBottomPadding: {
            true: {
                base: [
                    'pb-component-section-container-gutter'
                ]
            },
            false: {
                base: [
                    ''
                ]
            }
        },
        isSmallContainer: {
            true: {
                contentWrapper: [
                    'contentWrapper'
                ],
                contentWrapperInner: [
                    'contentWrapper'
                ]
            },
            false: {
                contentWrapper: [
                    ''
                ],
                contentWrapperInner: [
                    ''
                ]
            }
        }
    },
    defaultVariants: {
        layout: 'FullWidth',
        hasContent: false,
        backgroundLayout: 'FullWidth',
        isSmallContainer: false
    }
});
